/**
 * components/settings/SettingsNotificationsSection.tsx
 *
 * Settings section for managing email notification preferences.
 * Two independent toggles: immediate (per-completion) and daily digest.
 * Both can be active simultaneously, independently, or both off.
 * Created: 2026-03-30
 * Updated: replaced local NotificationPrefs interface with the exported type
 *   from useSettingsProfile to avoid drift between definitions.
 */

import Switch from '../ui/Switch';
import { type NotificationPrefs } from '../../hooks/useSettingsProfile';

interface SettingsNotificationsSectionProps {
  prefs: NotificationPrefs;
  loading: boolean;
  onUpdatePref: (key: keyof NotificationPrefs, value: boolean) => void;
}

interface NotificationRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

function NotificationRow({ title, description, checked, disabled, onChange }: NotificationRowProps) {
  return (
    <div className="flex items-start justify-between gap-6 py-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-900">{title}</p>
        <p className="text-sm text-neutral-500 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div className="flex-shrink-0 pt-0.5">
        <Switch checked={checked} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}

export default function SettingsNotificationsSection({
  prefs,
  loading,
  onUpdatePref,
}: SettingsNotificationsSectionProps) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 divide-y divide-neutral-100 px-6">
      <NotificationRow
        title="Notification immédiate"
        description="Recevez un email à chaque fois qu'un client complète un formulaire."
        checked={prefs.notification_immediate}
        disabled={loading}
        onChange={(val) => onUpdatePref('notification_immediate', val)}
      />
      <NotificationRow
        title="Résumé quotidien"
        description="Recevez chaque matin un email récapitulant les formulaires complétés la veille."
        checked={prefs.notification_daily_digest}
        disabled={loading}
        onChange={(val) => onUpdatePref('notification_daily_digest', val)}
      />
    </div>
  );
}
